/**
 * Ask before leaving a page with unsaved changes (the flow builder, #684).
 *
 * The app uses BrowserRouter, so react-router's useBlocker is not available.
 * Three exits are covered while `dirty` is true:
 *  - closing the tab or reloading (beforeunload — the browser's own prompt,
 *    the one dialog that cannot be replaced)
 *  - the browser Back button (a history sentinel, as in useLeaveGuard)
 *  - in-app navigation: react-router ends in history.pushState/replaceState,
 *    which are wrapped so a navigation away from this path asks first.
 *
 * The question itself is the in-page ConfirmDialog (useConfirm), so the
 * wrapped pushState returns at once without navigating; react-router's
 * listener then reads the unchanged URL and the screen stays. On yes the
 * navigation is issued again through the router with the guard let down.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../components/DialogProvider.jsx';

const SENTINEL = { kuutioUnsavedGuard: true };

export default function useUnsavedChangesGuard(dirty, message) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const messageRef = useRef(message);
  messageRef.current = message;
  const askRef = useRef(confirm);
  askRef.current = confirm;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!dirty) return undefined;
    const here = window.location.pathname;
    let allowed = false;
    let asking = false;
    const leaving = (url) => {
      if (url == null) return false;
      try {
        return new URL(String(url), window.location.href).pathname !== here;
      } catch {
        return false;
      }
    };
    // One question at a time: a second navigation while the dialog is open
    // is dropped, as the browser dialog dropped it by blocking.
    const ask = () => {
      if (asking) return Promise.resolve(null);
      asking = true;
      return askRef.current(messageRef.current).finally(() => { asking = false; });
    };

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = messageRef.current;
      return messageRef.current;
    };

    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    const wrap = (original, replace) => function guarded(state, title, url) {
      if (allowed || !leaving(url)) return original.call(window.history, state, title, url);
      ask().then((ok) => {
        if (!ok) return;
        allowed = true;
        // react-router's own history state keeps the caller's state in `usr`
        navigateRef.current(String(url), { replace, state: state?.usr ?? null });
      });
      return undefined;
    };
    window.history.pushState = wrap(originalPush, false);
    window.history.replaceState = wrap(originalReplace, true);

    // Back button: keep a sentinel entry on top; popping it asks first
    originalPush.call(window.history, SENTINEL, '', window.location.href);
    const onPopState = () => {
      ask().then((ok) => {
        if (ok) {
          window.removeEventListener('popstate', onPopState);
          window.history.back();
        } else if (ok === false) {
          originalPush.call(window.history, SENTINEL, '', window.location.href);
        }
      });
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
      // Drop our sentinel if it is still on top, so Back works normally again
      if (window.history.state?.kuutioUnsavedGuard) originalReplace.call(window.history, null, '', window.location.href);
    };
  }, [dirty]);
}
